import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Link,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../theme/theme';
import { COLORS } from '../../theme/themeConstants';
import AuthLayout from './AuthLayout';
import { useResetPassword } from '../Query/ResetPasswordQuery';
import { useResendOTP } from '../Query/ResendOTPQuery';
import ConfirmationDialog from './ConfirmationDialog';

const CODE_LENGTH = 6;
const RESEND_TIMER_SECONDS = 60; // Define timer duration

const ResetPassword: React.FC = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const channel = new URLSearchParams(location.search).get('channel');
  const rawEmail = new URLSearchParams(location.search).get('email');
  const rawPhone = new URLSearchParams(location.search).get('phone');

  const email = rawEmail ? decodeURIComponent(rawEmail) : null;
  const phone = rawPhone ? decodeURIComponent(rawPhone) : null;

  const [timeLeft, setTimeLeft] = useState(0); // State for timer
  const [isTimerActive, setIsTimerActive] = useState(false); // State for timer active status

  const previousHash = useRef(window.location.hash);
  const newHash = useRef<string | null>(null);

  const { mutate: ResetPassword, isPending: ResetPasswordLoading } =
    useResetPassword();

  const { mutate: ResendOTP, isPending: ResendOTPLoading } = useResendOTP();

  useEffect(() => {
    const handleHashChange = (event: HashChangeEvent) => {
      event.preventDefault();
      newHash.current = window.location.hash;
      window.location.hash = previousHash.current;
      setShowConfirmDialog(true);
      setPendingNavigation(false);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Timer useEffect
  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (isTimerActive && timeLeft > 0) {
      intervalId = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0 && isTimerActive) {
      setIsTimerActive(false);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isTimerActive, timeLeft]);

  // Effect to start timer on component mount
  React.useEffect(() => {
    setTimeLeft(RESEND_TIMER_SECONDS);
    setIsTimerActive(true);
  }, []); // Empty dependency array ensures this runs once on mount

  const handleChange = (value: string, idx: number) => {
    if (!/^[0-9a-zA-Z]?$/.test(value)) return;
    const newCode = [...code];
    newCode[idx] = value;
    setCode(newCode);
    if (value && idx < CODE_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const enteredCode = code.join('');
    const emailOrMobile = channel === 'email' ? {email: email} : {phoneNumber: phone}
    const modifiedValues = {
      ...emailOrMobile,
      channel: channel,
      otp: enteredCode,
    };
    ResetPassword(modifiedValues, {
      onSuccess: () => {
        navigate(
          `/set-new-password?email=${encodeURIComponent(email || '')}&code=${enteredCode}&channel=${channel}&phone=${encodeURIComponent(phone || '')}`
        );
      },
      onError: (error: any) => {
        console.log(error);
        // setErrors(parseJavaError(error));
      },
    });
  };

  const resendClick = (e: React.FormEvent) => {
    e.preventDefault();

    // Only resend if timer is not active
    if (!isTimerActive) {
      const modifiedValues = {
        email: email,
        channel: 'email',
      };
      ResendOTP(modifiedValues, {
        onSuccess: () => {
          // Start the timer on successful resend
          setTimeLeft(RESEND_TIMER_SECONDS);
          setIsTimerActive(true);
          // navigate(`/set-new-password?email=${encodeURIComponent(email || '')}&code=${enteredCode}`);
        },
        onError: (error: any) => {
          console.log(error);
          // Handle error, e.g., show a snackbar message
          // setErrors(parseJavaError(error));
        },
      });
    }
  };
  const handleBackToLogin = () => {
    setShowConfirmDialog(true);
    setPendingNavigation(true);
  };

  const handleConfirmNavigation = () => {
    setShowConfirmDialog(false);
    if (pendingNavigation && newHash.current) {
      window.location.hash = '#/';
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleCancelNavigation = () => {
    setShowConfirmDialog(false);
    setPendingNavigation(false);
    newHash.current = null;
    window.history.pushState(null, '', window.location.href);
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout>
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <span role="img" aria-label="mail" style={{ fontSize: 28 }}>
                  📧
                </span>
              </Box>
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 1, fontSize: 16 }}
            >
              Password reset
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#888', mb: 3, fontSize: 16 }}
            >
              We sent a code to <b>{channel === 'email' ? email : phone}</b>
            </Typography>
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mb: 2,
                  height: '60px',
                }}
              >
                {code.map((v, i) => (
                  // <TextField
                  //   key={i}
                  //   value={v}
                  //   onChange={(e) => handleChange(e.target.value, i)}
                  //   onKeyDown={(e) =>
                  //     handleKeyDown(
                  //       e as React.KeyboardEvent<HTMLInputElement>,
                  //       i
                  //     )
                  //   }
                  //   inputProps={{
                  //     maxLength: 1,
                  //     style: { textAlign: 'center', fontSize: 24, width: 55, padding: '15px 8px',},
                  //   }}
                  //   inputRef={(el) => (inputsRef.current[i] = el)}
                  //   InputProps={{
                  //     sx: { py: 1.5 }
                  //   }}
                  // />
                  <TextField
                    key={i}
                    value={v}
                    onChange={(e) => handleChange(e.target.value, i)}
                    onKeyDown={(e) =>
                      handleKeyDown(
                        e as React.KeyboardEvent<HTMLInputElement>,
                        i
                      )
                    }
                    inputRef={(el) => (inputsRef.current[i] = el)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '45px', // ✅ this works
                        fontSize: 16,
                        width: '45px', // Add font size to TextField input
                      },
                    }}
                    InputProps={{
                      inputProps: {
                        maxLength: 1,
                        style: {
                          textAlign: 'center',
                          fontSize: 24, // Keep this larger for the OTP digits themselves
                          width: 55,
                          padding: '15px 8px',
                        },
                      },
                    }}
                  />
                ))}
              </Box>
              {/* <Button
                type="submit"
                variant="contained"
                sx={{
                  width: '50%',
                  backgroundColor: COLORS.primary.main,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 16,
                  borderRadius: 2,
                  mb: 2,
                  py: 1.5,
                }}
                disabled={code.some((c) => !c)}
                >
                {ResetPasswordLoading || ResendOTPLoading
                  ? 'Sending...'
                  : 'Continue'}
              </Button> */}
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: COLORS.primary.main,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 16,
                  borderRadius: 2,
                  mb: 2,
                  py: 1.5,
                  px: 3, // Add horizontal padding
                  whiteSpace: 'nowrap', // Prevent text wrap
                  alignSelf: 'center', // Optional: center if inside a flex column
                }}
                disabled={code.some((c) => !c)}
              >
                {ResetPasswordLoading || ResendOTPLoading
                  ? 'Sending...'
                  : 'Continue'}
              </Button>
            </form>
            <Typography
              variant="body2"
              sx={{ color: '#888', mb: 1, fontSize: 16 }}
            >
              Didn't receive the OPT?{' '}
              {isTimerActive ? (
                <Typography
                  component="span"
                  sx={{ color: '#888', fontSize: 16 }}
                >
                  Resend in {timeLeft}s
                </Typography>
              ) : (
                <Link
                  component="button"
                  onClick={resendClick}
                  sx={{ fontSize: 16 }}
                >
                  Click to resend
                </Link>
              )}
            </Typography>
            <Link
              component="button"
              onClick={handleBackToLogin}
              sx={{
                color: '#888',
                fontSize: 16,
                mt: 1,
                textDecoration: 'none',
                fontFamily: 'Livvic',
              }}
            >
              ← Back to log in
            </Link>
            <ConfirmationDialog
              open={showConfirmDialog}
              title="Confirm Navigation"
              message="Are you sure you want to exit? Any unsaved changes will be lost."
              onConfirm={handleConfirmNavigation}
              onCancel={handleCancelNavigation}
            />
          </Paper>
        </Container>
      </AuthLayout>
    </ThemeProvider>
  );
};

export default ResetPassword;
